/* istanbul ignore file */
/**
 * ShoppingAssistantLazy.jsx
 *
 * Client-only wrapper for the Ask Penney AI Shopping Assistant.
 * Returns null on server to prevent ESM/CJS compatibility issues with Constructor packages.
 * Wrapped in ErrorBoundary to prevent crashes from affecting the rest of the page.
 */

import React from 'react';
import { string } from 'prop-types';

// Return null on server - Constructor packages are ESM-only and break SSR
const isServer = typeof window === 'undefined';

// Lazy import only on client
let ShoppingAssistantInnerLazy = null;
if (!isServer) {
    // Dynamic import only happens on client
    const loadable = require('@loadable/component').default;
    const { timeout } = require('../../lazy/lazyConstants');
    
    ShoppingAssistantInnerLazy = loadable(
        () => import(/* webpackChunkName: "AskPenneyInner" */ './ShoppingAssistantInner'),
        {
            fallback: null,
            timeout,
            ssr: false,
        }
    );
}

/**
 * Wrapper component that passes props through to the lazy-loaded inner component.
 * Returns null on server to avoid ESM errors.
 */
const ShoppingAssistantLazy = ({ apiKey, domain }) => {
    // Don't render anything on server
    if (isServer || !ShoppingAssistantInnerLazy) {
        return null;
    }

    // Lazy load ErrorBoundary only on client to keep server bundle clean
    const ErrorBoundary = require('../ErrorBoundary/ErrorBoundary').default;

    return (
        <ErrorBoundary errorTemplate={null}>
            <ShoppingAssistantInnerLazy apiKey={apiKey} domain={domain} />
        </ErrorBoundary>
    );
};

ShoppingAssistantLazy.propTypes = {
    apiKey: string,
    domain: string,
};

ShoppingAssistantLazy.defaultProps = {
    apiKey: undefined,
    domain: undefined,
};

export default ShoppingAssistantLazy;
